import React, { useCallback, useEffect, useState } from 'react';
import {
  BOARD_EDITORIAL_LIMITS,
  validateBoardEditorial,
  type AdminBoardEditorialDto,
  type AdminBoardTeamDto,
  type AdminBoardWorkDto,
  type BoardEditorialActionRequestDto,
} from '@living-library/contracts';
import { boardError, json, type BoardRequest } from './board-api';
import { button, c, card, input, LanguageEditor, Requirements, Status } from './board-ui';
import { BoardContentEditor } from './board-content-editor';
import { BoardPreview } from './board-preview';

export function localDateTime(value: string | null) {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
function iso(value: string) {
  return value ? new Date(value).toISOString() : null;
}
export function BoardEditor({
  initial,
  request,
  onClose,
}: {
  initial: AdminBoardEditorialDto;
  request: BoardRequest;
  onClose(): void;
}) {
  const [item, setItem] = useState(initial),
    [saved, setSaved] = useState(initial),
    [live, setLive] = useState(initial),
    [team, setTeam] = useState<AdminBoardTeamDto[]>([]);
  const [works, setWorks] = useState<Record<string, AdminBoardWorkDto>>({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [liveError, setLiveError] = useState('');
  const [publishAt, setPublishAt] = useState(localDateTime(initial.publishAt));
  const base = item.kind === 'picks' ? '/picks' : '/quizzes',
    dirty = !item.id || JSON.stringify(item) !== JSON.stringify(saved);
  const issues = validateBoardEditorial(item.kind, item),
    locked = live.hasVotes;
  const workResolved = useCallback(
    (work: AdminBoardWorkDto) => setWorks((current) => ({ ...current, [work.id]: work })),
    [],
  );
  useEffect(() => {
    void request<AdminBoardTeamDto[]>('/team')
      .then(setTeam)
      .catch((error) => setError(boardError(error)));
  }, [request, initial.kind]);
  useEffect(() => {
    if (!item.id || item.kind !== 'quiz') return;
    let current = true;
    const refresh = () =>
      void request<AdminBoardEditorialDto>(`${base}/${item.id}`)
        .then((value) => {
          if (current) {
            setLive(value);
            setLiveError('');
          }
        })
        .catch((error) => {
          if (current) setLiveError(boardError(error));
        });
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => {
      current = false;
      clearInterval(timer);
    };
  }, [request, base, item.id, item.kind]);
  const adopt = (value: AdminBoardEditorialDto) => {
    setItem(value);
    setSaved(value);
    setLive(value);
  };
  async function save() {
    setBusy(true);
    setError('');
    try {
      const body = {
        ...item,
        questions: item.questions.map((question) => ({
          ...question,
          options: question.options.filter((option) => option.workId),
        })),
      };
      adopt(
        await request<AdminBoardEditorialDto>(
          item.id ? `${base}/${item.id}` : base,
          json(item.id ? 'PUT' : 'POST', body),
        ),
      );
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  async function action(action: BoardEditorialActionRequestDto['action']) {
    setBusy(true);
    setError('');
    try {
      adopt(
        await request<AdminBoardEditorialDto>(
          `${base}/${item.id}/status`,
          json('POST', {
            action,
            expectedVersion: saved.expectedVersion,
            publishAt: item.kind === 'quiz' ? item.startsAt : iso(publishAt),
          }),
        ),
      );
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  async function reload() {
    setBusy(true);
    setError('');
    try {
      adopt(await request<AdminBoardEditorialDto>(`${base}/${item.id}`));
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    setError('');
    try {
      await request(`${base}/${item.id}?version=${saved.expectedVersion}`, { method: 'DELETE' });
      onClose();
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  const canPublish =
    !busy && !dirty && !issues.length && Boolean(item.hostId) && item.status !== 'ended';
  const scheduleValid =
    item.kind === 'quiz'
      ? Boolean(item.startsAt && Date.parse(item.startsAt) > Date.now())
      : Boolean(publishAt && new Date(publishAt).getTime() > Date.now());
  const publishValid =
    item.kind === 'picks' ||
    Boolean(
      item.startsAt &&
      Date.parse(item.startsAt) <= Date.now() &&
      item.endsAt &&
      Date.parse(item.endsAt) > Date.now(),
    );
  const saveValid =
    !busy &&
    item.items.every((pick) => pick.workId) &&
    (item.kind === 'picks'
      ? Boolean(item.period)
      : Boolean(item.startsAt && item.endsAt && item.endsAt > item.startsAt));
  return (
    <section>
      <button className={`${button} mb-5`} onClick={onClose} disabled={busy}>
        {c.back}
      </button>
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <h2 className="font-serif text-[clamp(1.75rem,3vw,2.25rem)] leading-tight">
          {item.kind === 'picks' ? c.picks : c.quizzes}
        </h2>
        <Status value={item.status} />
      </div>
      {error && (
        <p role="alert" className="mb-4 text-warning">
          {error}
        </p>
      )}
      <fieldset className="min-w-0" disabled={busy}>
        <div className={`${card} mb-5`}>
          {item.kind === 'picks' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <label>
                {c.month}
                <input
                  className={input}
                  type="month"
                  value={item.period ?? ''}
                  onChange={(event) => setItem({ ...item, period: event.target.value })}
                />
              </label>
              <label>
                {c.host}
                <select
                  className={input}
                  value={item.hostId ?? ''}
                  onChange={(event) => setItem({ ...item, hostId: event.target.value || null })}
                >
                  <option value="">{c.chooseHost}</option>
                  {team.map((member) => (
                    <option value={member.id} key={member.id}>
                      {member.displayName}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <>
              <LanguageEditor
                label={c.quizTitle}
                value={item.title}
                limit={BOARD_EDITORIAL_LIMITS.title}
                onChange={(title) => setItem({ ...item, title })}
              />
              <LanguageEditor
                label={c.introLabel}
                value={item.intro}
                limit={BOARD_EDITORIAL_LIMITS.intro}
                multiline
                onChange={(intro) => setItem({ ...item, intro })}
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <label>
                  {c.starts}
                  <input
                    className={input}
                    type="datetime-local"
                    value={localDateTime(item.startsAt)}
                    onChange={(event) => {
                      const startsAt = iso(event.target.value),
                        defaultEnd =
                          item.startsAt &&
                          item.endsAt &&
                          Date.parse(item.endsAt) - Date.parse(item.startsAt) === 14 * 86400000;
                      setItem({
                        ...item,
                        startsAt,
                        endsAt:
                          startsAt && defaultEnd
                            ? new Date(Date.parse(startsAt) + 14 * 86400000).toISOString()
                            : item.endsAt,
                      });
                    }}
                  />
                </label>
                <label>
                  {c.ends}
                  <input
                    className={input}
                    type="datetime-local"
                    value={localDateTime(item.endsAt)}
                    onChange={(event) => setItem({ ...item, endsAt: iso(event.target.value) })}
                  />
                </label>
                <label>
                  {c.host}
                  <select
                    className={input}
                    value={item.hostId ?? ''}
                    onChange={(event) => setItem({ ...item, hostId: event.target.value || null })}
                  >
                    <option value="">{c.chooseHost}</option>
                    {team.map((member) => (
                      <option value={member.id} key={member.id}>
                        {member.displayName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </>
          )}
        </div>
        <BoardContentEditor
          item={item}
          onChange={setItem}
          request={request}
          locked={locked}
          onWork={workResolved}
        />
      </fieldset>
      <Requirements issues={issues} dirty={dirty} />
      <div className={`${card} mt-5 flex flex-wrap items-end gap-3`}>
        <button className="admin-button-primary" disabled={!saveValid} onClick={() => void save()}>
          {busy ? c.loading : c.save}
        </button>
        <button
          className={button}
          disabled={!canPublish || !publishValid}
          onClick={() => void action('publish')}
        >
          {c.publish}
        </button>
        {item.kind === 'picks' && (
          <label className="text-sm">
            {c.publishAt}
            <input
              className={input}
              type="datetime-local"
              value={publishAt}
              onChange={(event) => setPublishAt(event.target.value)}
            />
          </label>
        )}
        <button
          className={button}
          disabled={!canPublish || !scheduleValid}
          onClick={() => void action('schedule')}
        >
          {c.schedule}
        </button>
        {item.id && (
          <>
            <button
              className={button}
              disabled={busy || dirty}
              onClick={() => void action(item.kind === 'picks' ? 'archive' : 'end')}
            >
              {item.kind === 'picks' ? c.archive : c.end}
            </button>
            {item.status === 'scheduled' && (
              <button
                className={button}
                disabled={busy || dirty || locked}
                onClick={() => void action('draft')}
              >
                {c.draft}
              </button>
            )}
            <button className={button} disabled={busy} onClick={() => void reload()}>
              {c.refresh}
            </button>
            {['draft', 'archived', 'ended'].includes(item.status) && !locked && (
              <button
                className="admin-button-danger"
                disabled={busy || dirty}
                onClick={() => void remove()}
              >
                {c.remove}
              </button>
            )}
          </>
        )}
      </div>
      {item.kind === 'quiz' && (
        <section className={`${card} mt-6`}>
          <h2 className="font-serif text-2xl">{c.counts}</h2>
          <p className="my-3">
            {live.answerCount} {c.answers}
          </p>
          {liveError && (
            <p role="alert" className="text-warning">
              {liveError}
            </p>
          )}
          {item.questions.map((question, index) => (
            <div className="mt-4" key={question.id}>
              <h3>
                {c.question} {index + 1}
              </h3>
              <ul className="mt-2 grid gap-2">
                {question.options.map((option) => (
                  <li className="flex justify-between gap-4 text-sm" key={option.id}>
                    <span>
                      {works[option.workId]?.editions.en?.title ??
                        works[option.workId]?.editions.sk?.title ??
                        option.workId}
                    </span>
                    <output className="font-bold text-brass-pale">
                      {live.counts.find((count) => count.optionId === option.id)?.count ?? 0}
                    </output>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
      <BoardPreview item={item} works={works} />
    </section>
  );
}
